/**
 * ICS-202 boxes 5, 7, 11, 12 and 13 are rich text (Tiptap HTML), mirroring
 * `ICS202_RICH_FIELDS` in
 * services/coms/src/routes/DGPT/draftForm/ics202Fields.ts.
 *
 * Images are incident files, stored as `<img src="pratus-file:<id>"
 * data-file-id="<id>">` so exports can turn them back into photo pages; the
 * editor swaps in a signed URL for display. Older plain-text values, with
 * `[[photo:...]]` markers, open as rich text.
 */
import MarkdownIt from 'markdown-it';

import { parsePhotoMarkers } from '../formPhotos/photoMarkers';

const markdown = new MarkdownIt({ breaks: true });

export const ICS202_RICH_FIELDS: ReadonlySet<string> = new Set([
  'priorities',
  'operationalPeriodCommandEmphasis',
  'criticalInformationRequirements',
  'limitationsAndConstraints',
  'keyDecisionsAndProcedures',
]);

export const isIcs202RichField = (source: string): boolean =>
  ICS202_RICH_FIELDS.has(source);

export const FILE_IMAGE_SCHEME = 'pratus-file:';

const BLOCK_HTML =
  /<(p|div|h[1-6]|ul|ol|li|table|blockquote|pre|img|hr|figure)\b/i;
const HAS_MARKDOWN = /[*_#`]|^\s*[-+]\s|^\s*\d+\.\s/m;

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const fileImageTag = (fileId: string, caption = ''): string =>
  `<img src="${FILE_IMAGE_SCHEME}${fileId}" data-file-id="${fileId}" alt="${escapeHtml(caption)}">`;

const plainToHtml = (text: string): string =>
  text
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');

/** A stored value as editor HTML; plain text and markdown become paragraphs. */
export const toEditorHtml = (value: string | null | undefined): string => {
  const text = value ?? '';
  if (!text.trim() || BLOCK_HTML.test(text)) return text;
  const photos: string[] = [];
  let withTokens = text;
  for (const marker of [...parsePhotoMarkers(text)].reverse()) {
    photos.unshift(fileImageTag(marker.fileId, marker.caption));
    withTokens = `${withTokens.slice(0, marker.start)}\n\n@@PHOTO@@\n\n${withTokens.slice(marker.end)}`;
  }
  const html = HAS_MARKDOWN.test(withTokens)
    ? markdown.render(withTokens)
    : plainToHtml(withTokens);
  let index = 0;
  return html
    .replace(/<p>\s*@@PHOTO@@\s*<\/p>/g, () => photos[index++] ?? '')
    .replace(/@@PHOTO@@/g, () => photos[index++] ?? '');
};

/** Editor HTML as stored: file images point at their file id, not a signed URL. */
export const toStoredHtml = (html: string): string =>
  html.replace(/<img\b[^>]*>/gi, (tag) => {
    const fileId = /\sdata-file-id="([^"]+)"/i.exec(tag)?.[1];
    if (!fileId) return tag;
    return tag
      .replace(/\s(src|width)="[^"]*"/gi, '')
      .replace(/^<img\b/i, `<img src="${FILE_IMAGE_SCHEME}${fileId}"`);
  });

/** Ids of the incident files shown as images in the value. */
export const richImageFileIds = (html: string | null | undefined): string[] => {
  const ids = new Set<string>();
  for (const match of (html ?? '').matchAll(/data-file-id="([^"]+)"/g)) {
    ids.add(match[1].toLowerCase());
  }
  for (const marker of parsePhotoMarkers(html)) ids.add(marker.fileId);
  return [...ids];
};
